import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { ShoppingCart, Package, Users as UsersIcon, Building2, FileText, BarChart2, Briefcase, ChevronRight, Receipt } from "lucide-react";

const TILES = [
    { icon: ShoppingCart, label: "POS Sales", to: "/business/more/sales", color: "#00D084" },
    { icon: Package, label: "Inventory", to: "/business/more/products", color: "#0066FF" },
    { icon: UsersIcon, label: "Customers", to: "/business/more/customers", color: "#FF6B00" },
    { icon: Building2, label: "Suppliers", to: "/business/more/suppliers", color: "#7000FF" },
    { icon: Receipt, label: "Expenses", to: "/business/more/expenses", color: "#FF0055" },
    { icon: FileText, label: "Invoices", to: "/business/more/invoices", color: "#06B6D4" },
    { icon: BarChart2, label: "Reports", to: "/business/finance", color: "#F59E0B" },
    { icon: Briefcase, label: "Staff", to: "/business/more", color: "#8B5CF6" },
];

export default function MerchantOS() {
    const [overview, setOverview] = useState(null);
    const [products, setProducts] = useState([]);

    useEffect(() => {
        api.get("/business/overview").then((r) => setOverview(r.data)).catch(() => {});
        api.get("/business/products").then((r) => setProducts(r.data)).catch(() => {});
    }, []);

    const top = [...products].sort((a, b) => (b.units_sold || 0) - (a.units_sold || 0)).slice(0, 3);

    return (
        <div data-testid="merchantos-page">
            <MobileHeader title="MerchantOS™" subtitle="Run your business" backTo="/business" variant="blue" />
            <div className="px-5 -mt-6 pb-4 space-y-4">
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow">
                    <div className="grid grid-cols-4 gap-3">
                        {TILES.map((t) => (
                            <Link to={t.to} key={t.label} data-testid={`mos-${t.label.replace(/\s/g, "")}`} className="text-center tap-feedback">
                                <div className="size-12 rounded-xl flex items-center justify-center mx-auto" style={{ background: `${t.color}15`, color: t.color }}>
                                    <t.icon size={20} />
                                </div>
                                <div className="text-[11px] font-bold mt-1.5">{t.label}</div>
                            </Link>
                        ))}
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Today's Summary</div>
                    <div className="grid grid-cols-3 gap-3 mt-3">
                        <Stat label="Sales" value={fmtCurrency(overview?.total_sales ? overview.total_sales / 30 : 0, "NGN", { compact: true })} />
                        <Stat label="Transactions" value={String(Math.round((overview?.transactions_count || 0) / 30))} />
                        <Stat label="Average Sale" value={fmtCurrency(overview?.total_sales && overview?.transactions_count ? overview.total_sales / overview.transactions_count : 0, "NGN", { compact: true })} />
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="font-display font-semibold text-[#0A1930] mb-3">Top Selling Products</div>
                    <div className="space-y-3">
                        {top.length === 0 && <div className="text-sm text-slate-500 text-center py-4">No products yet</div>}
                        {top.map((p) => (
                            <div key={p.id} className="flex items-center gap-3" data-testid={`top-prod-${p.id}`}>
                                <div className="size-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                                    <Package size={18} />
                                </div>
                                <div className="flex-1">
                                    <div className="font-semibold text-sm">{p.name}</div>
                                    <div className="text-xs text-slate-500">{p.units_sold || 0} units sold</div>
                                </div>
                                <div className="font-semibold text-sm">{fmtCurrency(p.price)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{label}</div>
            <div className="font-display font-bold mt-1">{value}</div>
        </div>
    );
}
